import { useState, useEffect } from "react";
import { parseJSONFunc } from "../../setup/util/helpers";

// Define a generic type for the value stored in localStorage
type LocalStorageValue<T> = T | null;

// Define a custom hook
function useLocalStorage<T>(
  key: string,
  initialValue: T
): [LocalStorageValue<T>, (value: T) => void] {
  // Get initial value from localStorage, if available
  const storedValue: LocalStorageValue<T> = (() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? parseJSONFunc(item) : initialValue;
    } catch (error) {
      console.error(`Error fetching data from localStorage: ${error}`);
      return initialValue;
    }
  })();

  // Set up state to track the current value
  const [value, setValue] = useState<LocalStorageValue<T>>(storedValue);

  // Define a function to update the value in localStorage and state
  const updateValue = (newValue: T) => {
    try {
      // Save to local storage
      if (typeof newValue === "string")
        window.localStorage.setItem(key, newValue);
      else window.localStorage.setItem(key, JSON.stringify(newValue));
      // Update state
      setValue(newValue);
    } catch (error) {
      console.error(`Error saving data to localStorage: ${error}`);
    }
  };

  // Listen for changes to the key in localStorage and update the state
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        setValue(event.newValue ? parseJSONFunc(event.newValue) : null);
      }
    };

    // Subscribe to storage events
    window.addEventListener("storage", handleStorageChange);

    // Clean up subscription
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key]);

  return [value, updateValue];
}

export default useLocalStorage;
